import type { Middleware } from '@reduxjs/toolkit';

type PerfAction = { type: string; dur: number; ts: number };

type PerfState = {
    enabled: boolean;
    renders: Record<string, number>;
    actions: Array<PerfAction>;
    tags: Record<string, number>;
};

declare global {
    interface Window {
        __PERF__?: PerfState;
        __STORE__?: unknown;
    }
}

const isBrowser = (): boolean => typeof window !== 'undefined';

export const isPerfEnabled = (): boolean => {
    if (!isBrowser()) return false;
    try {
        return new URLSearchParams(window.location.search).has('perf');
    } catch {
        return false;
    }
};

export const initPerfState = (): void => {
    if (!isBrowser() || !isPerfEnabled()) return;
    window.__PERF__ = { enabled: true, renders: {}, actions: [], tags: {} };
};

export const exposeStoreForPerf = (store: unknown): void => {
    if (!isBrowser() || !isPerfEnabled()) return;
    window.__STORE__ = store;
};

export const recordWidgetRender = (uid: string): void => {
    if (!isBrowser()) return;
    const perf = window.__PERF__;
    if (!perf?.enabled || !uid) return;
    perf.renders[uid] = (perf.renders[uid] ?? 0) + 1;
    console.log('render', uid, perf.renders[uid]);
};

export const recordPerfTag = (tag: string): void => {
    if (!isBrowser()) return;
    const perf = window.__PERF__;
    if (!perf?.enabled || !tag) return;
    perf.tags[tag] = (perf.tags[tag] ?? 0) + 1;
};

export const perfMiddleware: Middleware = () => (next) => (action) => {
    const perf = isBrowser() ? window.__PERF__ : undefined;
    if (!perf?.enabled) return next(action);
    const start = performance.now();
    const result = next(action);
    const type = typeof action === 'object' && action !== null && 'type' in action ? String((action as { type: unknown }).type) : 'unknown';
    perf.actions.push({ type, dur: performance.now() - start, ts: start });
    return result;
};
